"use client";

import React, { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Alert, Box, CircularProgress } from "@mui/material";
import { Header } from "@components/header";
import * as Sentry from "@sentry/nextjs";
import CanvasEditor from "src/components/canvas/CanvasEditor";
import { fetchCanvasDefinitionId } from "src/components/canvas/resolve-canvas-definition";

/**
 * The stable public entry point to the canvas editor: /{locale}/canvas.
 *
 * This is the URL to put in ads, emails and on the website. The editor itself
 * is keyed by a product id that differs per environment, so linking to
 * /canvas/<uuid> from outside breaks on stage and dies whenever that row is
 * recreated. Here the id is resolved at load time instead.
 *
 * No order is needed to arrive: CanvasEditor creates one on the first upload.
 */
export default function CanvasEntryPage() {
  const t = useTranslations("editor");
  const [definitionId, setDefinitionId] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const id = await fetchCanvasDefinitionId();
        if (cancelled) return;
        if (id) setDefinitionId(id);
        else setFailed(true);
      } catch (err) {
        if (cancelled) return;
        Sentry.captureException(err);
        setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (failed) {
    return (
      <>
        <Header sticky={false} />
        <Box sx={{ p: 4 }}>
          <Alert severity="error">{t("productNotFound")}</Alert>
        </Box>
      </>
    );
  }

  if (!definitionId) {
    return (
      <>
        <Header sticky={false} />
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return <CanvasEditor definitionId={definitionId} />;
}
